import { SearchTypeDetails } from "@me8eon/data_views";
import { useSelectedDataView } from "@me8eon/data_view_filter";
import { Aggregates, BaseDataSourceDetails, DataSources, FiltersPagingAndQuery, SearchResult, useDataSources } from "@me8eon/data_sources";
import { useCallback, useEffect, useMemo } from "react";
import { LensBuilder, lensBuilder } from "@me8eon/optics";
import { GetterSetter, makeContextForState } from "@me8eon/context";
import { DebugLog, useDebug } from "@me8eon/debug";
import { BooleanFeatureFlag, useFeatureFlag } from "@me8eon/featureflags";
import { searchDebugName } from "@me8eon/filter_data_widget";
import { NameAnd } from "@me8eon/record_utils";
import { ErrorsOr, isErrors, valueOrDefault } from "@me8eon/errors";

import { FeedbackPayload, useFeedback } from "@me8eon/feedback";
import { SearchTextFilter, searchTextName } from "@me8eon/searchtext_filters";
import { interleave } from "@me8eon/arrays";
import { useDebouncedCallback } from "@me8eon/debounce";
import { useUserData } from "@me8eon/react_authentication";
import { UserData } from "@me8eon/authentication";


export const immediateSearch = "immediate";
export const submitSearch = "submit";
export const aggregationsSearch = "aggregations";
export const generateFilterFromData = "generateFilterFromData";

export type OneDataSourceSearchResult<Filters, Paging, Query, Results> = {
    filters: Filters
    dataSourceName: string
    names: string[]
    page: number
    paging?: Paging
    query: Query
    results: Promise<Results>
}

// The search type came from the data view
type SearchConfig = {
    debug: DebugLog
    doSearch?: boolean
    scroll: boolean
}

export const oneDataSourceSearch = <Filters, Paging, Query, Results>(dataSources: DataSources<Filters>, filters: Filters, paging: Paging | undefined, page: number, { debug, doSearch = true, scroll }: SearchConfig, userData: UserData) =>
    (details: BaseDataSourceDetails): OneDataSourceSearchResult<Filters, Paging, Query, Results> => {
        const { type: dataSourceName, names = [] } = details;
        const source = dataSources[dataSourceName];
        if (!source) throw new Error(`No source found for ${dataSourceName}. Legal values are ${Object.keys(dataSources || {})}`);
        const filtersAndQuery: FiltersPagingAndQuery<Filters, Paging, Query> = source.query({ filters, paging, names, scroll, debug, userData });
        const results = source.search(filtersAndQuery.query, debug);
        return { ...filtersAndQuery, dataSourceName, page, names, results };
    };

type OneSearchResult<Filters, Paging, Query, Results> = NameAnd<OneDataSourceSearchResult<Filters, Paging, Query, Results>>

export type SearchFn<Filters, Paging, Query, Results> = (dataSources: DataSources<Filters>, filters: Filters, searchType: SearchTypeDetails<BaseDataSourceDetails>, config: SearchConfig, userData: UserData) => OneSearchResult<Filters, Paging, Query, Results>

export function search<Filters, Paging, Query, Results>(dataSources: DataSources<Filters>,
    filters: Filters,
    searchType: SearchTypeDetails<BaseDataSourceDetails>,
    config: SearchConfig,
    userData: UserData): OneSearchResult<Filters, Paging, Query, Results> {
    if (!searchType) throw new Error(`No search type`);
    const dataSourceDetails = searchType.dataSource;
    return Object.fromEntries(dataSourceDetails.map(detail =>
        [detail.type, oneDataSourceSearch<Filters, Paging, Query, Results>(dataSources, filters, undefined, 0, config, userData)(detail)]));
}

export type SearchFiltersAndResults<Filters, Paging, Query, Results> = {
    /**The count is here to stop race conditions. Bad scenario: query1 => takes 10 seconds, user changes to query 2 => 1 second. The results of query 2 might be shown, then overwritten by the results of query 1. Count allows us to avoid that*/
    count: number
    /** The filters that were active at the time of the search. This is valuable for debugging but also for scrolling*/
    filters: Filters
    /** The debug information about the search. This is useful for debugging and showing the user what is going on. Also we use it while paging as this is 'the search that is in progress'*/
    debug: OneSearchResult<Filters, Paging, Query, Results>
    /** The results of the search. This is a map of the data source name to the results. This is useful for debugging and showing the user what is going on*/
    searchResults: NameAnd<ErrorsOr<SearchResult<Paging, Results>>>
}

export function emptySearchFiltersAndResults<Filters, Paging, Query, Results>(filters: Filters): SearchFiltersAndResults<Filters, Paging, Query, Results> {
    return { count: 0, filters, debug: {}, searchResults: {} };
}

export type AllSearchData<SearchType extends string, Filters, Paging, Query, Results> = Record<SearchType, SearchFiltersAndResults<Filters, Paging, Query, Results>>
export const { use: useAllSearchData, Provider: AllSearchDataProvider } = makeContextForState<AllSearchData<any, any, any, any, any>, "allSearchData">("allSearchData");
export const { use: useSearchResults, Provider: SearchResultsProvider } = makeContextForState<any[], "searchResults">("searchResults");

export function useSearchResultsFromAllSearchData<SearchType extends string>(searchType: SearchType) {
    const [allSearch] = useAllSearchData();
    const result = useMemo(() => {
        const data = Object.values(allSearch[searchType].searchResults || {}).map(d => isErrors(d) ? [] : d.value.results as any[]);
        const res: any[] = interleave(data);
        return res;
    }, [allSearch]);
    return result;
}

let count = 0;

export function useCombinedAggregates<SearchType extends string>(st: SearchType): Aggregates {
    const [allSearch] = useAllSearchData();

    const result = useMemo(() => {
        const allSr = allSearch[st].searchResults;
        const result: Aggregates = {};
        for (const [name, sr] of Object.entries(allSr)) {
            if (isErrors(sr)) continue;
            const indexToAggregates = sr.value.aggregates;
            for (const [index, aggregates] of Object.entries(indexToAggregates)) {
                for (const [name, agg] of Object.entries(aggregates)) {
                    result[name] = agg;
                }
            }
        }
        return result;
    }, [count++]);
    return result;

}

export type TriggerSearch<Filters> = (searchType: string, filters: Filters) => void;

export function useTriggerSearch<Filters>(): TriggerSearch<Filters> {
    const [_, setAllSearch] = useAllSearchData();
    const trigger: TriggerSearch<Filters> = useCallback((name: string, filters: Filters) => {
        if (name === "submit")
            setAllSearch(old => lensForGuiFilters<string, Filters, any, any, any>("aggregations").set(old, filters));
        setAllSearch(old => lensForGuiFilters<string, Filters, any, any, any>(name).set(old, filters));
    }, [setAllSearch]);
    return trigger;
}

export function lensForGuiFilters<SearchType extends string, Filters, Paging, Query, Results>(searchType: SearchType) {
    return lensBuilder<AllSearchData<SearchType, Filters, Paging, Query, Results>>().focusOn(searchType).focusOn("filters");
}

type DebugCountAndResult<Filters, Paging, Query, Results> = { debug: OneSearchResult<Filters, Paging, Query, Results>, results: NameAnd<ErrorsOr<SearchResult<Paging, Results>>>, count: number }

function lensForDebug<SearchType extends string, Filters, Paging, Query, Results>(searchType: SearchType): LensBuilder<AllSearchData<SearchType, Filters, Paging, Query, Results>, OneSearchResult<Filters, Paging, Query, Results>> {
    const lb = lensBuilder<AllSearchData<SearchType, Filters, Paging, Query, Results>>();
    const stL = lb.focusOn(searchType);
    return stL.focusOn("debug");
}

function lensForDebugCountAndResult<SearchType extends string, Filters, Paging, Query, Results>(searchType: SearchType): LensBuilder<AllSearchData<SearchType, Filters, Paging, Query, Results>, DebugCountAndResult<Filters, Paging, Query, Results>> {
    const lb = lensBuilder<AllSearchData<SearchType, Filters, Paging, Query, Results>>();
    const stL = lb.focusOn(searchType);
    const result: LensBuilder<AllSearchData<SearchType, Filters, Paging, Query, Results>, DebugCountAndResult<Filters, Paging, Query, Results>> =
        lb.focusCompose({
            count: stL.focusOn("count"),
            debug: stL.focusOn("debug"),
            results: stL.focusOn("searchResults"),
        });
    return result;
}


function lensForResults<SearchType extends string, Filters, Paging, Query, Results>(searchType: SearchType, name: string): LensBuilder<AllSearchData<SearchType, Filters, Paging, Query, Results>, ErrorsOr<SearchResult<Paging, Results>>> {
    const lb: LensBuilder<AllSearchData<SearchType, Filters, Paging, Query, Results>, NameAnd<ErrorsOr<SearchResult<Paging, Results>>>> =
        lensBuilder<AllSearchData<SearchType, Filters, Paging, Query, Results>>().focusOn(searchType).focusOn("searchResults");
    return lb.focusOn(name);
}


const srLens = lensBuilder<SearchResult<any, any>>().focusOn("results");

export function updateOneSearchDataFromSearchResult<SearchType extends string, Filters, Paging, Query, Results>(
    dataSources: DataSources<Filters>,
    st: SearchType,
    name: string,
    countWhenStarting: number,
    page: number,
    srOps: GetterSetter<AllSearchData<SearchType, Filters, Paging, Query, Results>>,
    d: OneDataSourceSearchResult<Filters, Paging, Query, Results>,
    scroll: boolean,
    debugLog: DebugLog) {

    const [sr, setSr] = srOps;
    const lens = lensForResults<SearchType, Filters, Paging, Query, Results>(st, name);

    function fullResponse(st: SearchType, name: string, old: AllSearchData<SearchType, Filters, Paging, Query, Results>, response: SearchResult<Paging, Results>): ErrorsOr<SearchResult<Paging, Results>> {
        if (scroll) {
            // if (!response.paging) return old[st].searchResults[name];
            const oldDataAndErrors = old[st].searchResults[name];
            if (isErrors(oldDataAndErrors)) return oldDataAndErrors;
            const oldData = oldDataAndErrors.value.results as any[];
            const value = srLens.set(response, [...oldData, ...response.results as any[]]);
            return { value };
        }
        return { value: response };
    }

    return d.results.then(r => {
        const ds = dataSources[name];
        const rawResponse = ds.results(r, name, debugLog);
        const response: SearchResult<Paging, Results> = { ...rawResponse, page: d.page };
        setSr(old => old[st].count === countWhenStarting ? lens.set(old, fullResponse(st, name, old, response)) : old);
    }).catch(e => {
        debugLog.debugError(e);
        setSr(old => old[st].count === countWhenStarting ? lens.set(old, { errors: [e.message] }) : old);
    });

}

export type UpdateAllSearchDataFromDebug<Filters, Paging, Query, Results, SearchType extends string> =
    (dataSources: DataSources<any>,
        st: SearchType,
        srOps: GetterSetter<AllSearchData<SearchType, Filters, Paging, Query, Results>>,
        scroll: boolean,
        countWhenStarting: number,
        debug: OneSearchResult<Filters, Paging, Query, Results>,
        debugLog: DebugLog) => void;

export function updateAllSearchDataFromDebug<Filters, Paging, Query, Results, SearchType extends string>(dataSources: DataSources<any>,
    st: SearchType,
    srOps: GetterSetter<AllSearchData<SearchType, Filters, Paging, Query, Results>>,
    scroll: boolean,
    countWhenStarting: number,
    debug: OneSearchResult<Filters, Paging, Query, Results>,
    debugLog: DebugLog) {
    if (debug)
        Object.entries(debug).map(([name, d]) =>
            updateOneSearchDataFromSearchResult(dataSources, st, name, countWhenStarting, 0, srOps, d, scroll, debugLog));
}

export type DoSearchForTest<Filters, Paging, Query, Results, SearchType extends string, UserData> = {
    _search: SearchFn<Filters, Paging, Query, Results>
    _updateAllSearchDataFromDebug: UpdateAllSearchDataFromDebug<Filters, Paging, Query, Results, SearchType>
}

export function useDoSearch<SearchType extends string, Filters extends SearchTextFilter, Paging, Query, Results>
    (st: SearchType,
        feedback?: (filters: Filters) => FeedbackPayload | null,
        { _search, _updateAllSearchDataFromDebug }: DoSearchForTest<Filters, Paging, Query, Results, SearchType, UserData> = { _search: search, _updateAllSearchDataFromDebug: updateAllSearchDataFromDebug }) {
    const dataSources = useDataSources();
    const srOps = useAllSearchData();
    const [sr, setSr] = srOps;
    const dataView = useSelectedDataView<SearchType, any>();
    const filters: Filters = sr[st].filters;
    const countWhenStarting = sr[st].count + 1;
    const debugSearch = useDebug(searchDebugName);
    const feedbackFn = useFeedback();
    const userData = useUserData();
    const doTheSearch = useCallback(() => {
        if (!userData.loggedIn) return;
        if (!filters) return;
        if (!filters[searchTextName]) return;//we don't search unless we have a search text
        const stDetails = dataView.search[st];
        if (!stDetails) return;
        const f: any = feedback?.(filters);
        if (f && f?.searchType !== "immediate") feedbackFn({ ...f, role: userData.currentRole });
        const debug: OneSearchResult<Filters, Paging, Query, Results> = _search(dataSources, filters, stDetails, { debug: debugSearch, doSearch: true, scroll: false }, userData);
        _updateAllSearchDataFromDebug(dataSources, st, srOps, false, countWhenStarting, debug, debugSearch);
        setSr(old => lensForDebugCountAndResult<SearchType, Filters, Paging, Query, Results>(st).set(old, { debug, results: {}, count: countWhenStarting }));
    }, [filters, dataView, userData]);
    const debounded = useDebouncedCallback(doTheSearch, 50);
    useEffect(debounded, [filters, dataView, userData.loggedIn]);
}

export function useDataForFilters<SearchType extends string, Filters extends SearchTextFilter, Paging, Query, Results>
    (st: SearchType,
        feedback?: (filters: Filters) => FeedbackPayload | null,
        { _search, _updateAllSearchDataFromDebug }: DoSearchForTest<Filters, Paging, Query, Results, SearchType, UserData> = { _search: search, _updateAllSearchDataFromDebug: updateAllSearchDataFromDebug }) {
    const srOps = useAllSearchData();
    const [sr, setSr] = srOps;
    const dataView = useSelectedDataView<SearchType, any>();
    const filters: Filters = sr[st].filters;
    const userData = useUserData();

    const generateFilter = useCallback(() => {
        const resArray: string[] = [];
        const dataArray: any = sr.submit.searchResults.graphApiPeople;

        if (!dataArray) {
            console.log("Error in generateFilterFromData", dataArray);
            return;
        }
        dataArray.value.results.forEach((obj: any) => {
            if (resArray.indexOf(obj.resource.department) < 0) {
                resArray.push(obj.resource.department);
            };
        });

        const aggrSearchResults: any = {
            "graphSearchAggregates": {
                "value": {
                    "paging": {},
                    "results": [],
                    "finished": true,
                    "aggregates": {
                        "hits": [],
                        "total": resArray.length,
                        "moreResultsAvailable": false,
                        "aggregates": {
                            "departmentType": resArray
                        }
                    },
                    "page": 0
                }
            }
        }
        setSr({
            ...sr,
            aggregations: {
                ...sr.submit,
                searchResults: aggrSearchResults
            },
        });
        return;
    }, [filters, dataView, userData]);
    const debounded = useDebouncedCallback(generateFilter, 50);
    useEffect(debounded, [filters, dataView, userData.loggedIn]);
}

export function scroll<SearchType extends string, Filters, Paging, Query, Results>(dataSources: DataSources<any>,
    st: SearchType,
    details: BaseDataSourceDetails,
    srOps: GetterSetter<AllSearchData<SearchType, Filters, Paging, Query, Results>>,
    debugLog: DebugLog,
    userData: UserData) {
    const [sr, setSr] = srOps;
    const filters = sr[st].filters;
    const config: SearchConfig = { debug: debugLog, scroll: true, doSearch: true };
    const value = valueOrDefault(sr[st].searchResults[details.type], {} as any);
    const paging: Paging | undefined = value?.paging;
    const pageInProgress = sr[st].debug[details.type]?.page;
    const currentDisplayedPage: number = value?.page;
    const finished = value?.finished;
    const exitEarly = finished || currentDisplayedPage === undefined || (pageInProgress !== undefined && (pageInProgress > currentDisplayedPage));
    debugLog("scroll", { filters, paging, pageInProgress, currentDisplayedPage, exitEarly, finished });
    if (exitEarly) return;
    const page = currentDisplayedPage + 1;
    const oneResult = oneDataSourceSearch<Filters, Paging, Query, Results>(dataSources, filters, paging, page, config, userData)(details);
    const oldDebug = sr[st].debug;
    const newDebug = { ...oldDebug, [details.type]: oneResult };
    debugLog("scroll", "newDebug", newDebug);
    const countAtStart = sr[st].count;
    setSr(old => lensForDebug<SearchType, Filters, Paging, Query, Results>(st).set(old, newDebug));
    return updateOneSearchDataFromSearchResult(dataSources, st, details.type, countAtStart, page, srOps, oneResult, true, debugLog);
}